/**
 * Allowlist for message HTML shown via `[innerHTML]`.
 *
 * Angular's default sanitizer strips `style`, which is how Quill stores color and highlight.
 * This keeps only Safe_Color `color` / `background-color` / `background` declarations, plus the
 * tags/attrs the composer already emits (bold, mentions, links, images).
 *
 * Keep the Safe_Color regex in sync with `InputSanitizer.kt`.
 */

// hex | rgb/rgba | hsl/hsla | named
const SAFE_CSS_COLOR = new RegExp(
  '^(#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|' +
    'rgba?\\(\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*(?:,\\s*(?:0|1|0?\\.\\d+|1\\.0)\\s*)?\\)|' +
    'hsla?\\(\\s*\\d{1,3}(?:\\.\\d+)?\\s*,\\s*\\d{1,3}(?:\\.\\d+)?%\\s*,\\s*\\d{1,3}(?:\\.\\d+)?%\\s*' +
    '(?:,\\s*(?:0|1|0?\\.\\d+|1\\.0)\\s*)?\\)|' +
    '[a-zA-Z]{1,20})$'
);

const EMPHASIS_PROPERTIES = new Set(['color', 'background-color', 'background']);

const DROP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'LINK', 'META', 'BASE',
  'SVG', 'MATH', 'VIDEO', 'AUDIO', 'SOURCE', 'TEMPLATE'
]);

/** Tags Quill (and Word/Outlook paste after server sanitization) actually emit. */
const ALLOWED_TAGS = new Set([
  'A', 'B', 'BLOCKQUOTE', 'BR', 'DIV', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'I', 'IMG', 'LI', 'OL', 'P', 'S', 'SPAN', 'STRIKE', 'STRONG', 'SUB', 'SUP', 'U', 'UL'
]);

const URL_ATTRIBUTES = new Set(['href', 'src']);

const ATTRS_BY_TAG: Record<string, Set<string>> = {
  A: new Set(['href']),
  IMG: new Set(['src', 'alt']),
  SPAN: new Set(['class', 'style', 'data-id', 'data-value', 'data-denotation-char', 'data-index'])
};

const SAFE_SRC_DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

/** Drop C0/C1 controls so `java&#10;script:` cannot hide a scheme. */
function stripControls(value: string): string {
  let out = '';
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x20 || (code >= 0x7f && code <= 0x9f)) {
      continue;
    }
    out += value[i];
  }
  return out;
}

export function isSafeCssColor(value: string): boolean {
  return SAFE_CSS_COLOR.test(value.trim());
}

export function retainEmphasisStyles(style: string): string | null {
  const kept: string[] = [];
  for (const declaration of style.split(';')) {
    const separator = declaration.indexOf(':');
    if (separator <= 0) {
      continue;
    }
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const cssValue = declaration.slice(separator + 1).trim();
    if (!EMPHASIS_PROPERTIES.has(property) || !isSafeCssColor(cssValue)) {
      continue;
    }
    kept.push(`${property}: ${cssValue}`);
  }
  return kept.length ? kept.join('; ') : null;
}

function schemeOf(value: string): string | null {
  const normalized = stripControls(value).toLowerCase();
  const colon = normalized.indexOf(':');
  if (colon <= 0) {
    return null;
  }
  return normalized.slice(0, colon);
}

function isSafeUrlAttribute(name: string, value: string): boolean {
  const scheme = schemeOf(value);
  if (scheme === null) {
    return true;
  }
  if (name === 'src') {
    if (scheme === 'http' || scheme === 'https') {
      return true;
    }
    if (scheme === 'data') {
      return SAFE_SRC_DATA_IMAGE.test(stripControls(value));
    }
    return false;
  }
  return scheme === 'http' || scheme === 'https' || scheme === 'mailto' || scheme === 'tel';
}

function allowedAttributes(tag: string): Set<string> {
  return ATTRS_BY_TAG[tag] ?? new Set(['style']);
}

function sanitizeClass(value: string): string | null {
  const tokens = value.split(/\s+/).filter(token => token === 'mention' || token.startsWith('ql-'));
  return tokens.length ? tokens.join(' ') : null;
}

function unwrapUnknownTags(root: HTMLElement): void {
  Array.from(root.querySelectorAll('*')).reverse().forEach(el => {
    if (DROP_TAGS.has(el.tagName)) {
      el.remove();
      return;
    }
    if (!ALLOWED_TAGS.has(el.tagName)) {
      el.replaceWith(...Array.from(el.childNodes));
    }
  });
}

function sanitizeAttributes(el: Element): void {
  const allowed = allowedAttributes(el.tagName);
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on') || !allowed.has(name)) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (name === 'class') {
      const keptClass = sanitizeClass(attr.value);
      if (keptClass) {
        el.setAttribute('class', keptClass);
      } else {
        el.removeAttribute(attr.name);
      }
      continue;
    }
    if (name === 'style') {
      const keptStyle = retainEmphasisStyles(attr.value);
      if (keptStyle) {
        el.setAttribute('style', keptStyle);
      } else {
        el.removeAttribute(attr.name);
      }
      continue;
    }
    if (URL_ATTRIBUTES.has(name) && !isSafeUrlAttribute(name, attr.value)) {
      el.removeAttribute(attr.name);
    }
  }
}

export function allowMessageEmphasisStyles(html: string | null | undefined): string {
  if (!html) {
    return '';
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  unwrapUnknownTags(doc.body);
  doc.body.querySelectorAll('*').forEach(el => sanitizeAttributes(el));
  return doc.body.innerHTML;
}
